import type {
  ApprovalRequest,
  AuditRecord,
  ConfigSetting,
  IntegrationRecord,
  JobRecord,
  LaunchCheck,
  PermissionRecord,
  RoleRecord,
  SafetySwitch,
  StaffInvitation,
  StaffMember,
  Tenant,
  WebhookRecord,
} from '../types/governance'

/* --------------------------------------------------------------- permissions */

export const permissionRegister: PermissionRecord[] = [
  {
    key: 'users.view',
    label: 'View user accounts',
    domain: 'Users',
    dataClass: 'internal',
    note: 'Profile, account status and affiliation. Not identity documents, notes or wellbeing records.',
  },
  {
    key: 'users.suspend',
    label: 'Suspend or restrict an account',
    domain: 'Users',
    dataClass: 'confidential',
    requiresStepUp: true,
    note: 'Restrictions are capability-level. A reason is mandatory and the person is told what changed.',
  },
  {
    key: 'users.identity.view',
    label: 'Open identity documents',
    domain: 'Users',
    dataClass: 'highly_restricted',
    requiresStepUp: true,
    note: 'Every open is written to the audit log against your name, whether or not you act on it.',
  },
  {
    key: 'institutions.verify',
    label: 'Verify an institution',
    domain: 'Institutions',
    dataClass: 'internal',
    note: 'Requires a named source and evidence. Submission alone never produces verification.',
  },
  {
    key: 'campuses.edit',
    label: 'Edit campus configuration',
    domain: 'Campuses',
    dataClass: 'internal',
    note: 'Timezone, currency, contacts, facilities and local policy for campuses inside your scope.',
  },
  {
    key: 'campuses.launch',
    label: 'Activate a campus',
    domain: 'Campuses',
    dataClass: 'confidential',
    requiresApproval: true,
    requiresStepUp: true,
    note: 'Blocked while any mandatory launch check is outstanding. Needs a second approver.',
  },
  {
    key: 'experience.design.manage',
    label: 'Publish design configuration',
    domain: 'Experience',
    dataClass: 'internal',
    note: 'Chooses which approved design a public surface presents. It changes presentation only — never product data, pricing, verification or permissions.',
  },
  {
    key: 'platform.settings.manage',
    label: 'Change platform settings',
    domain: 'Platform',
    dataClass: 'internal',
    note: 'Platform identity, website metadata, branding and localisation. Cannot reach database credentials or server secrets — no such action exists.',
  },
  {
    key: 'platform.route.manage',
    label: 'Change the administration URL',
    domain: 'Platform',
    dataClass: 'confidential',
    requiresStepUp: true,
    note: 'Moves where administration answers. The previous URL stops working immediately, so recovery is a server-side command rather than a screen.',
  },
  {
    key: 'platform.security.manage',
    label: 'Change security policy',
    domain: 'Platform',
    dataClass: 'confidential',
    requiresApproval: true,
    note: 'Session length, password floor, throttling and administrator MFA. Never the secrets themselves.',
  },
  {
    key: 'housing.listings.verify',
    label: 'Verify a housing listing',
    domain: 'Housing',
    dataClass: 'internal',
    note: 'Marks a property Campuzon Verified for a fixed period against representative evidence.',
  },
  {
    key: 'marketplace.listings.approve',
    label: 'Approve a marketplace listing',
    domain: 'Marketplace',
    dataClass: 'internal',
    note: 'Publishes a listing to buyers within your scope.',
  },
  {
    key: 'finance.transactions.view',
    label: 'Inspect transactions',
    domain: 'Finance',
    dataClass: 'restricted',
    note: 'Full ledger entries and provider references. Never available to support or domain staff.',
  },
  {
    key: 'finance.transactions.refund',
    label: 'Prepare a refund',
    domain: 'Finance',
    dataClass: 'restricted',
    requiresApproval: true,
    note: 'Prepares a compensating entry. A different person with finance approval must release it.',
  },
  {
    key: 'ledger.reconcile',
    label: 'Run reconciliation',
    domain: 'Finance',
    dataClass: 'restricted',
    note: 'Recomputes ledger against provider and settlement records. Reports drift; changes nothing.',
  },
  {
    key: 'payouts.approve',
    label: 'Approve a payout',
    domain: 'Finance',
    dataClass: 'restricted',
    requiresApproval: true,
    requiresStepUp: true,
    note: 'Releases an instruction to a licensed provider. Never available to whoever prepared it.',
  },
  {
    key: 'campagift.configure',
    label: 'Configure CampaGift rules',
    domain: 'CampaGift',
    dataClass: 'restricted',
    requiresApproval: true,
    note: 'Gift types, expiry, transferability, commission and redemption caps. Never outstanding value.',
  },
  {
    key: 'risk.review',
    label: 'Review a risk case',
    domain: 'Risk',
    dataClass: 'restricted',
    note: 'Signals and case history. A score is an input to human judgement, never a finding.',
  },
  {
    key: 'moderation.resolve',
    label: 'Resolve a moderation case',
    domain: 'Trust & safety',
    dataClass: 'confidential',
    note: 'Reporter identity stays withheld where they asked for it.',
  },
  {
    key: 'support.tickets.view',
    label: 'Work support tickets',
    domain: 'Support',
    dataClass: 'internal',
    note: 'The ticket and the record it names. Financial references arrive masked.',
  },
  {
    key: 'staff.assign',
    label: 'Assign administrative roles',
    domain: 'Governance',
    dataClass: 'restricted',
    requiresStepUp: true,
    note: 'Privileged roles enter an approval queue rather than activating on assignment.',
  },
  {
    key: 'staff.approve',
    label: 'Approve a role assignment',
    domain: 'Governance',
    dataClass: 'restricted',
    requiresStepUp: true,
    note: 'You cannot approve an assignment you requested.',
  },
  {
    key: 'config.edit',
    label: 'Change platform configuration',
    domain: 'Configuration',
    dataClass: 'confidential',
    note: 'Taxonomy, policy and limits. Every change records its previous value.',
  },
  {
    key: 'flags.edit',
    label: 'Change feature flags',
    domain: 'Configuration',
    dataClass: 'confidential',
    note: 'Enable, pause or pilot a capability by tenant, institution or campus.',
  },
  {
    key: 'security.emergency',
    label: 'Use emergency controls',
    domain: 'Security',
    dataClass: 'highly_restricted',
    requiresStepUp: true,
    note: 'Pause a regulated capability or lock administrative access. Alerts are sent immediately.',
  },
  {
    key: 'audit.view',
    label: 'Read the audit log',
    domain: 'Audit',
    dataClass: 'restricted',
    note: 'Append-only. No administrative role can delete or edit an audit record.',
  },
  {
    key: 'data.export',
    label: 'Export a dataset',
    domain: 'Data',
    dataClass: 'restricted',
    requiresApproval: true,
    note: 'A stated purpose is mandatory and the export itself is logged.',
  },
]

/* --------------------------------------------------------------------- roles */

export const roleRegister: RoleRecord[] = [
  {
    id: 'role-super',
    name: 'Global super administrator',
    origin: 'platform',
    domain: 'Governance',
    privileged: true,
    description:
      'Platform governance. Holds no exemption from audit — every action is recorded like anyone else’s.',
    permissions: [
      'users.view',
      'users.suspend',
      'institutions.verify',
      'campuses.edit',
      'campuses.launch',
      'staff.assign',
      'staff.approve',
      'config.edit',
      'flags.edit',
      'experience.design.manage',
      'platform.settings.manage',
      'platform.route.manage',
      'platform.security.manage',
      'audit.view',
    ],
  },
  {
    id: 'role-security',
    name: 'Security administrator',
    origin: 'platform',
    domain: 'Security',
    privileged: true,
    description: 'Sessions, credentials, escalation attempts and emergency controls.',
    permissions: ['security.emergency', 'audit.view', 'users.suspend', 'flags.edit'],
  },
  {
    id: 'role-finance',
    name: 'Finance administrator',
    origin: 'platform',
    domain: 'Finance',
    privileged: true,
    description:
      'Ledger, reconciliation and provider references. Cannot set a balance — no such action exists.',
    permissions: [
      'finance.transactions.view',
      'finance.transactions.refund',
      'ledger.reconcile',
      'audit.view',
    ],
  },
  {
    id: 'role-finance-approver',
    name: 'Finance approver',
    origin: 'platform',
    domain: 'Finance',
    privileged: true,
    description: 'Releases refunds and payouts prepared by someone else. Deliberately separate.',
    permissions: ['finance.transactions.view', 'payouts.approve', 'audit.view'],
  },
  {
    id: 'role-campagift',
    name: 'CampaGift administrator',
    origin: 'platform',
    domain: 'CampaGift',
    description: 'Gift types, rules, merchants and settlement configuration.',
    permissions: ['campagift.configure', 'finance.transactions.view'],
  },
  {
    id: 'role-housing',
    name: 'Housing administrator',
    origin: 'platform',
    domain: 'Housing',
    description: 'Properties, listings, applications and housing verification.',
    permissions: ['housing.listings.verify', 'users.view', 'moderation.resolve'],
  },
  {
    id: 'role-marketplace',
    name: 'Marketplace administrator',
    origin: 'platform',
    domain: 'Marketplace',
    description: 'Listings, categories, providers, orders and disputes.',
    permissions: ['marketplace.listings.approve', 'users.view', 'moderation.resolve'],
  },
  {
    id: 'role-trust',
    name: 'Trust & safety administrator',
    origin: 'platform',
    domain: 'Trust & safety',
    description: 'Reports, moderation queues, appeals and safety incidents.',
    permissions: ['moderation.resolve', 'users.view', 'users.suspend', 'risk.review'],
  },
  {
    id: 'role-risk',
    name: 'Risk administrator',
    origin: 'platform',
    domain: 'Risk',
    privileged: true,
    description: 'Risk cases and signals. Documents decisions; does not label people.',
    permissions: ['risk.review', 'users.view', 'finance.transactions.view'],
  },
  {
    id: 'role-support',
    name: 'Support administrator',
    origin: 'platform',
    domain: 'Support',
    description:
      'Tickets and user enquiries. Deliberately holds no finance, risk or identity permission.',
    permissions: ['support.tickets.view', 'users.view'],
  },
  {
    id: 'role-admissions',
    name: 'Admissions administrator',
    origin: 'platform',
    domain: 'Admissions',
    description: 'Programmes, requirements, deadlines, scholarships and admissions content.',
    permissions: ['institutions.verify', 'users.view', 'config.edit'],
  },
  {
    id: 'role-audit',
    name: 'Audit administrator',
    origin: 'platform',
    domain: 'Audit',
    privileged: true,
    description: 'Reads the audit log and exports it. Cannot alter a single record.',
    permissions: ['audit.view', 'data.export'],
  },
  {
    id: 'role-campus-housing-officer',
    name: 'Campus housing officer',
    origin: 'custom',
    domain: 'Housing',
    description:
      'Created for UNILAG Akoka. Housing work on one campus only, with no verification authority.',
    permissions: ['housing.listings.verify', 'users.view'],
    createdBy: '@ops.lead',
  },
]

/* ------------------------------------------------------------------- tenants */

export const tenants: Tenant[] = [
  {
    id: 'tnt-campuzon',
    name: 'Campuzon Corporate',
    kind: 'campuzon',
    status: 'active',
    country: 'Nigeria',
    currency: 'NGN',
    createdAt: '2025-09-01T00:00:00Z',
    institutions: [
      {
        id: 'inst-platform',
        name: 'Platform operations',
        verified: true,
        verificationSource: 'Campuzon incorporation records',
        campuses: [
          {
            id: 'cmp-hq',
            name: 'Operations HQ',
            city: 'Lagos',
            timezone: 'Africa/Lagos',
            status: 'live',
            faculties: [],
          },
        ],
      },
    ],
  },
  {
    id: 'tnt-unilag',
    name: 'University of Lagos',
    kind: 'institution',
    status: 'active',
    country: 'Nigeria',
    currency: 'NGN',
    createdAt: '2025-11-14T00:00:00Z',
    institutions: [
      {
        id: 'inst-unilag',
        name: 'University of Lagos',
        verified: true,
        verificationSource: 'NUC register + institutional agreement',
        campuses: [
          {
            id: 'cmp-akoka',
            name: 'Akoka Campus',
            city: 'Lagos',
            timezone: 'Africa/Lagos',
            status: 'live',
            faculties: ['Engineering', 'Science', 'Social Sciences'],
          },
          {
            id: 'cmp-idiaraba',
            name: 'Idi-Araba Campus',
            city: 'Lagos',
            timezone: 'Africa/Lagos',
            status: 'in_progress',
            faculties: ['Clinical Sciences'],
          },
        ],
      },
    ],
  },
  {
    id: 'tnt-futa',
    name: 'Federal University of Technology, Akure',
    kind: 'institution',
    status: 'active',
    country: 'Nigeria',
    currency: 'NGN',
    createdAt: '2026-02-02T00:00:00Z',
    institutions: [
      {
        id: 'inst-futa',
        name: 'FUTA',
        verified: true,
        verificationSource: 'NUC register',
        campuses: [
          {
            id: 'cmp-futa-main',
            name: 'Main Campus',
            city: 'Akure',
            timezone: 'Africa/Lagos',
            status: 'ready_for_review',
            faculties: ['Engineering', 'Agriculture'],
          },
        ],
      },
    ],
  },
  {
    id: 'tnt-oau',
    name: 'Obafemi Awolowo University',
    kind: 'institution',
    status: 'onboarding',
    country: 'Nigeria',
    currency: 'NGN',
    createdAt: '2026-07-19T00:00:00Z',
    institutions: [
      {
        id: 'inst-oau',
        name: 'OAU',
        verified: false,
        verificationSource: null,
        campuses: [
          {
            id: 'cmp-oau-main',
            name: 'Main Campus',
            city: 'Ile-Ife',
            timezone: 'Africa/Lagos',
            status: 'not_started',
            faculties: [],
          },
        ],
      },
    ],
  },
]

/* --------------------------------------------------------------------- staff */

export const staffDirectory: StaffMember[] = [
  {
    userId: 'usr-0001',
    handle: '@ops.lead',
    name: 'Ifeoma Nwosu',
    status: 'active',
    mfaEnabled: true,
    lastLoginAt: '2026-08-14T07:12:00Z',
    lastReviewedAt: '2026-06-30T00:00:00Z',
    assignments: [
      {
        id: 'asg-1',
        roleId: 'role-super',
        scope: { level: 'global' },
        assignedBy: 'Bootstrap — recorded at platform creation',
        assignedAt: '2025-09-01T00:00:00Z',
        expiresAt: null,
        state: 'active',
        approvedBy: '@security.lead',
      },
    ],
  },
  {
    userId: 'usr-0002',
    handle: '@mary.f',
    name: 'Mary Fadeyi',
    status: 'active',
    mfaEnabled: true,
    lastLoginAt: '2026-08-13T16:40:00Z',
    lastReviewedAt: '2026-07-15T00:00:00Z',
    assignments: [
      {
        id: 'asg-2',
        roleId: 'role-finance',
        scope: { level: 'global', domain: 'finance' },
        assignedBy: '@ops.lead',
        assignedAt: '2026-01-12T00:00:00Z',
        expiresAt: null,
        state: 'active',
        approvedBy: '@security.lead',
      },
    ],
  },
  {
    userId: 'usr-0003',
    handle: '@john.a',
    name: 'John Adeyemi',
    status: 'active',
    mfaEnabled: true,
    lastLoginAt: '2026-08-14T06:02:00Z',
    lastReviewedAt: null,
    assignments: [
      {
        id: 'asg-3',
        roleId: 'role-housing',
        scope: {
          level: 'campus',
          tenantId: 'tnt-futa',
          institutionId: 'inst-futa',
          campusId: 'cmp-futa-main',
          domain: 'housing',
        },
        assignedBy: '@ops.lead',
        assignedAt: '2026-03-04T00:00:00Z',
        expiresAt: '2026-12-31T00:00:00Z',
        state: 'active',
      },
    ],
  },
  {
    userId: 'usr-0004',
    handle: '@david.o',
    name: 'David Okafor',
    status: 'active',
    mfaEnabled: false,
    lastLoginAt: '2026-08-12T09:31:00Z',
    lastReviewedAt: null,
    assignments: [
      {
        id: 'asg-4',
        roleId: 'role-support',
        scope: {
          level: 'campus',
          tenantId: 'tnt-unilag',
          institutionId: 'inst-unilag',
          campusId: 'cmp-akoka',
        },
        assignedBy: '@ops.lead',
        assignedAt: '2026-05-20T00:00:00Z',
        expiresAt: '2026-08-01T00:00:00Z',
        state: 'active',
      },
    ],
  },
  {
    userId: 'usr-0005',
    handle: '@adaeze.k',
    name: 'Adaeze Kalu',
    status: 'active',
    mfaEnabled: true,
    lastLoginAt: '2026-08-14T05:55:00Z',
    lastReviewedAt: '2026-07-15T00:00:00Z',
    assignments: [
      {
        id: 'asg-5',
        roleId: 'role-trust',
        scope: { level: 'global' },
        assignedBy: '@ops.lead',
        assignedAt: '2026-02-01T00:00:00Z',
        expiresAt: null,
        state: 'active',
      },
      {
        id: 'asg-6',
        roleId: 'role-risk',
        scope: { level: 'global' },
        assignedBy: '@ops.lead',
        assignedAt: '2026-08-10T00:00:00Z',
        expiresAt: null,
        state: 'awaiting_approval',
      },
    ],
  },
  {
    userId: 'usr-0006',
    handle: '@tunde.b',
    name: 'Tunde Bello',
    status: 'suspended',
    mfaEnabled: true,
    lastLoginAt: '2026-07-02T11:20:00Z',
    lastReviewedAt: '2026-07-03T00:00:00Z',
    assignments: [
      {
        id: 'asg-7',
        roleId: 'role-marketplace',
        scope: { level: 'tenant', tenantId: 'tnt-unilag' },
        assignedBy: '@ops.lead',
        assignedAt: '2026-04-11T00:00:00Z',
        expiresAt: null,
        state: 'active',
      },
    ],
  },
]

/** The signed-in administrator for this console session. */
export const currentStaffHandle = '@ops.lead'

export const invitations: StaffInvitation[] = [
  {
    id: 'inv-1',
    email: 'k.ogundele@futa.edu.ng',
    name: 'Kelechi Ogundele',
    roleId: 'role-campus-housing-officer',
    scope: {
      level: 'campus',
      tenantId: 'tnt-futa',
      institutionId: 'inst-futa',
      campusId: 'cmp-futa-main',
      domain: 'housing',
    },
    invitedBy: '@ops.lead',
    invitedAt: '2026-08-12T10:00:00Z',
    expiresAt: '2026-08-19T10:00:00Z',
    status: 'pending',
    tokenConsumed: false,
  },
  {
    id: 'inv-2',
    email: 'a.bello@unilag.edu.ng',
    name: 'Amina Bello',
    roleId: 'role-admissions',
    scope: { level: 'institution', tenantId: 'tnt-unilag', institutionId: 'inst-unilag' },
    invitedBy: '@ops.lead',
    invitedAt: '2026-07-28T09:00:00Z',
    expiresAt: '2026-08-04T09:00:00Z',
    status: 'expired',
    tokenConsumed: false,
  },
]

/* ----------------------------------------------------------------- approvals */

export const approvalQueue: ApprovalRequest[] = [
  {
    id: 'apr-1',
    kind: 'role_assignment',
    summary: 'Risk administrator (global) for @adaeze.k',
    requestedBy: '@ops.lead',
    requestedAt: '2026-08-10T14:00:00Z',
    scope: { level: 'global' },
    state: 'requested',
    reason: 'Taking over risk case review while the current administrator is on leave.',
    decidedBy: null,
    decidedAt: null,
    decisionReason: null,
    permissionToApprove: 'staff.approve',
  },
  {
    id: 'apr-2',
    kind: 'refund',
    summary: 'Refund ₦18,500 — duplicate knowledge licence charge',
    requestedBy: '@mary.f',
    requestedAt: '2026-08-13T11:20:00Z',
    scope: { level: 'global' },
    state: 'requested',
    reason: 'Duplicate accepted before idempotency covered that route. Compensating entry prepared.',
    decidedBy: null,
    decidedAt: null,
    decisionReason: null,
    permissionToApprove: 'payouts.approve',
  },
  {
    id: 'apr-3',
    kind: 'campus_launch',
    summary: 'Activate FUTA Main Campus',
    requestedBy: '@john.a',
    requestedAt: '2026-08-13T08:00:00Z',
    scope: {
      level: 'campus',
      tenantId: 'tnt-futa',
      institutionId: 'inst-futa',
      campusId: 'cmp-futa-main',
    },
    state: 'requested',
    reason: 'Pilot complete. Requesting production activation.',
    decidedBy: null,
    decidedAt: null,
    decisionReason: null,
    permissionToApprove: 'campuses.launch',
  },
  {
    id: 'apr-4',
    kind: 'data_export',
    summary: 'Export housing applications — FUTA, last 90 days',
    requestedBy: '@john.a',
    requestedAt: '2026-08-09T15:30:00Z',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    state: 'approved',
    reason: 'Occupancy planning for the coming session.',
    decidedBy: '@ops.lead',
    decidedAt: '2026-08-10T09:00:00Z',
    decisionReason: 'Scope is limited to their own campus and the purpose is operational.',
    permissionToApprove: 'data.export',
  },
]

/* --------------------------------------------------------------- launch checks */

const LIVE = ['cmp-hq', 'cmp-akoka']

export const launchChecks: LaunchCheck[] = [
  {
    id: 'chk-admin',
    label: 'Campus administrator assigned',
    requirement: 'At least one active administrator scoped to this campus.',
    severity: 'blocker',
    domain: 'Governance',
    satisfiedFor: [...LIVE, 'cmp-futa-main'],
  },
  {
    id: 'chk-institution',
    label: 'Institution verified',
    requirement: 'A named verification source and evidence on the institution record.',
    severity: 'blocker',
    domain: 'Institutions',
    satisfiedFor: [...LIVE, 'cmp-futa-main', 'cmp-idiaraba'],
  },
  {
    id: 'chk-safety',
    label: 'Emergency safety contacts configured',
    requirement: 'A reachable campus security contact and an escalation path.',
    severity: 'blocker',
    domain: 'Safety',
    satisfiedFor: [...LIVE, 'cmp-futa-main'],
  },
  {
    id: 'chk-moderation',
    label: 'Moderation staff assigned',
    requirement: 'Someone must own the moderation queue before students can post.',
    severity: 'blocker',
    domain: 'Trust & safety',
    satisfiedFor: [...LIVE],
  },
  {
    id: 'chk-support',
    label: 'Support channel configured',
    requirement: 'A support queue with an owner, so tickets cannot land nowhere.',
    severity: 'blocker',
    domain: 'Support',
    satisfiedFor: [...LIVE, 'cmp-futa-main'],
  },
  {
    id: 'chk-privacy',
    label: 'Privacy configuration completed',
    requirement: 'Visibility defaults and data-sharing agreement recorded for this institution.',
    severity: 'blocker',
    domain: 'Compliance',
    satisfiedFor: [...LIVE, 'cmp-futa-main'],
  },
  {
    id: 'chk-notify',
    label: 'Notification channels configured',
    requirement: 'Email at minimum. Without it, verification codes cannot be delivered.',
    severity: 'blocker',
    domain: 'Notifications',
    satisfiedFor: [...LIVE, 'cmp-futa-main', 'cmp-idiaraba'],
  },
  {
    id: 'chk-academic',
    label: 'Academic structure configured',
    requirement: 'Faculties, departments, programmes and the academic calendar.',
    severity: 'warning',
    domain: 'Academics',
    satisfiedFor: [...LIVE, 'cmp-futa-main'],
  },
  {
    id: 'chk-housing',
    label: 'Housing configuration completed',
    requirement: 'Areas, property types and verification radius for this campus.',
    severity: 'warning',
    domain: 'Housing',
    satisfiedFor: [...LIVE, 'cmp-futa-main'],
  },
  {
    id: 'chk-marketplace',
    label: 'Marketplace policy set',
    requirement: 'Categories, review policy and fee configuration.',
    severity: 'warning',
    domain: 'Marketplace',
    satisfiedFor: [...LIVE],
  },
  {
    id: 'chk-financial',
    label: 'Financial capabilities configured',
    requirement:
      'Provider selection and reconciliation configuration. Regulated capabilities stay paused regardless.',
    severity: 'warning',
    domain: 'Finance',
    satisfiedFor: [],
  },
  {
    id: 'chk-testing',
    label: 'End-to-end testing recorded',
    requirement: 'Registration, housing search, support and moderation exercised on this campus.',
    severity: 'info',
    domain: 'Quality',
    satisfiedFor: [...LIVE, 'cmp-futa-main'],
  },
]

/* -------------------------------------------------------------- configuration */

export const configSettings: ConfigSetting[] = [
  {
    id: 'cfg-1',
    label: 'Marketplace listing review policy',
    domain: 'Marketplace',
    value: 'Review before publish',
    origin: 'global',
    overridden: false,
    inheritedValue: null,
    changedBy: '@ops.lead',
    changedAt: '2026-04-02T00:00:00Z',
    impact: 'New listings stay in the moderation queue until an administrator approves them.',
    reversible: true,
    requiresApproval: false,
  },
  {
    id: 'cfg-2',
    label: 'Housing verification radius',
    domain: 'Housing',
    value: '250 m',
    origin: 'global',
    overridden: false,
    inheritedValue: null,
    changedBy: '@ops.lead',
    changedAt: '2026-03-18T00:00:00Z',
    impact:
      'Evidence captured beyond this distance from the expected location is flagged for human review.',
    reversible: true,
    requiresApproval: false,
  },
  {
    id: 'cfg-3',
    label: 'Event posting policy',
    domain: 'Community',
    value: 'Campus moderators approve',
    origin: 'campus',
    overridden: true,
    inheritedValue: 'Open posting',
    changedBy: '@ops.lead',
    changedAt: '2026-06-11T00:00:00Z',
    impact: 'Akoka events queue for a campus moderator instead of publishing immediately.',
    reversible: true,
    requiresApproval: false,
  },
  {
    id: 'cfg-4',
    label: 'CampaGift default expiry',
    domain: 'CampaGift',
    value: '12 months from issuance',
    origin: 'global',
    overridden: false,
    inheritedValue: null,
    changedBy: '@mary.f',
    changedAt: '2026-05-30T00:00:00Z',
    impact:
      'Applies to gifts issued after the change only. Existing instruments keep the expiry they were issued with.',
    reversible: false,
    requiresApproval: true,
  },
  {
    id: 'cfg-5',
    label: 'Marketplace commission',
    domain: 'Marketplace',
    value: '5% of order value',
    origin: 'tenant',
    overridden: true,
    inheritedValue: '7% of order value',
    changedBy: '@ops.lead',
    changedAt: '2026-07-01T00:00:00Z',
    impact: 'Applies to new orders. Orders already in escrow settle at the rate they were placed under.',
    reversible: false,
    requiresApproval: true,
  },
]

/* --------------------------------------------------------------------- health */

export const integrations: IntegrationRecord[] = [
  {
    id: 'int-1',
    provider: 'Havazon Mail',
    purpose: 'Transactional email — verification codes, security alerts',
    environment: 'production',
    health: 'healthy',
    configured: true,
    lastSuccessAt: '2026-08-14T07:40:00Z',
    lastFailureAt: '2026-08-11T02:14:00Z',
    webhookVerified: true,
    credentialRotatedAt: '2026-06-01T00:00:00Z',
  },
  {
    id: 'int-2',
    provider: 'Havazon Cloud storage',
    purpose: 'Documents, evidence media, note attachments',
    environment: 'production',
    health: 'healthy',
    configured: true,
    lastSuccessAt: '2026-08-14T07:44:00Z',
    lastFailureAt: null,
    webhookVerified: true,
    credentialRotatedAt: '2026-05-14T00:00:00Z',
  },
  {
    id: 'int-3',
    provider: 'Payment processor',
    purpose: 'Account funding and payout execution',
    environment: 'staging',
    health: 'unknown',
    configured: false,
    lastSuccessAt: null,
    lastFailureAt: null,
    webhookVerified: false,
    credentialRotatedAt: null,
  },
  {
    id: 'int-4',
    provider: 'Bill aggregator',
    purpose: 'Airtime, data, electricity and internet',
    environment: 'staging',
    health: 'unknown',
    configured: false,
    lastSuccessAt: null,
    lastFailureAt: null,
    webhookVerified: false,
    credentialRotatedAt: null,
  },
  {
    id: 'int-5',
    provider: 'SMS gateway',
    purpose: 'Phone verification and safety alerts',
    environment: 'production',
    health: 'degraded',
    configured: true,
    lastSuccessAt: '2026-08-14T06:10:00Z',
    lastFailureAt: '2026-08-14T07:35:00Z',
    webhookVerified: true,
    credentialRotatedAt: '2026-02-20T00:00:00Z',
  },
  {
    id: 'int-6',
    provider: 'Havazon AI',
    purpose: 'Assistant, transcription and summarisation',
    environment: 'production',
    health: 'healthy',
    configured: true,
    lastSuccessAt: '2026-08-14T07:39:00Z',
    lastFailureAt: '2026-08-13T22:02:00Z',
    webhookVerified: false,
    credentialRotatedAt: '2026-07-04T00:00:00Z',
  },
]

export const webhookEvents: WebhookRecord[] = [
  {
    id: 'whk-1',
    provider: 'Havazon Mail',
    event: 'message.delivered',
    receivedAt: '2026-08-14T07:40:12Z',
    state: 'processed',
    attempts: 1,
    detail: 'Signature verified. Delivery recorded against the verification request.',
  },
  {
    id: 'whk-2',
    provider: 'SMS gateway',
    event: 'message.failed',
    receivedAt: '2026-08-14T07:35:02Z',
    state: 'retried',
    attempts: 3,
    detail: 'Carrier rejected the number. Escalated to the notification owner after the third attempt.',
  },
  {
    id: 'whk-3',
    provider: 'Havazon Mail',
    event: 'message.bounced',
    receivedAt: '2026-08-13T18:22:41Z',
    state: 'dead_lettered',
    attempts: 5,
    detail: 'Address does not exist. Held for inspection rather than discarded.',
  },
  {
    id: 'whk-4',
    provider: 'Havazon AI',
    event: 'job.completed',
    receivedAt: '2026-08-13T22:03:10Z',
    state: 'failed',
    attempts: 2,
    detail: 'Signature could not be verified. Payload rejected without processing.',
  },
]

export const backgroundJobs: JobRecord[] = [
  {
    id: 'job-1',
    name: 'Nightly ledger reconciliation',
    queue: 'finance',
    state: 'succeeded',
    startedAt: '2026-08-14T02:00:00Z',
    attempts: 1,
    owner: 'Finance operations',
    error: null,
    nextRetryAt: null,
  },
  {
    id: 'job-2',
    name: 'Verification expiry sweep',
    queue: 'housing',
    state: 'succeeded',
    startedAt: '2026-08-14T03:15:00Z',
    attempts: 1,
    owner: 'Housing operations',
    error: null,
    nextRetryAt: null,
  },
  {
    id: 'job-3',
    name: 'Campus launch check evaluation',
    queue: 'operations',
    state: 'running',
    startedAt: '2026-08-14T07:30:00Z',
    attempts: 1,
    owner: 'Platform operations',
    error: null,
    nextRetryAt: null,
  },
  {
    id: 'job-4',
    name: 'SMS delivery retry',
    queue: 'notifications',
    state: 'retrying',
    startedAt: '2026-08-14T07:35:10Z',
    attempts: 3,
    owner: 'Notification operations',
    error: 'Carrier rejected the destination number.',
    nextRetryAt: '2026-08-14T08:05:00Z',
  },
  {
    id: 'job-5',
    name: 'Staff access expiry review',
    queue: 'governance',
    state: 'failed',
    startedAt: '2026-08-14T01:00:00Z',
    attempts: 2,
    owner: 'Security administration',
    error: 'One assignment has no expiry date and could not be evaluated.',
    nextRetryAt: '2026-08-14T09:00:00Z',
  },
]

export const safetySwitches: SafetySwitch[] = [
  {
    id: 'sw-funding',
    label: 'Account funding',
    capability: 'funding',
    state: 'unavailable',
    effect: 'No provider is configured, so this cannot be enabled from here at all.',
    permission: 'security.emergency',
    lastChangedBy: null,
    lastChangedAt: null,
  },
  {
    id: 'sw-transfers',
    label: 'User transfers',
    capability: 'transfers',
    state: 'paused',
    effect: 'New transfers are refused. Existing ledger entries are untouched.',
    permission: 'security.emergency',
    lastChangedBy: '@ops.lead',
    lastChangedAt: '2026-01-08T00:00:00Z',
  },
  {
    id: 'sw-escrow',
    label: 'Escrow initiation',
    capability: 'escrow',
    state: 'paused',
    effect:
      'No new order can enter escrow. Orders already in escrow continue through their state machine.',
    permission: 'security.emergency',
    lastChangedBy: '@ops.lead',
    lastChangedAt: '2026-01-08T00:00:00Z',
  },
  {
    id: 'sw-payouts',
    label: 'Provider payouts',
    capability: 'payouts',
    state: 'paused',
    effect: 'No instruction reaches a provider. Approved earnings stay approved and unpaid.',
    permission: 'security.emergency',
    lastChangedBy: '@ops.lead',
    lastChangedAt: '2026-01-08T00:00:00Z',
  },
  {
    id: 'sw-gift-redemption',
    label: 'CampaGift redemption',
    capability: 'campagift_redemption',
    state: 'enabled',
    effect:
      'Pausing refuses new redemptions at every merchant desk. Remaining gift value is preserved exactly.',
    permission: 'security.emergency',
    lastChangedBy: '@ops.lead',
    lastChangedAt: '2026-06-22T00:00:00Z',
  },
  {
    id: 'sw-bills',
    label: 'Bill payments',
    capability: 'bills',
    state: 'unavailable',
    effect: 'No bill provider is configured.',
    permission: 'security.emergency',
    lastChangedBy: null,
    lastChangedAt: null,
  },
  {
    id: 'sw-admin-lock',
    label: 'Administrative access lock',
    capability: 'admin_access',
    state: 'enabled',
    effect:
      'Locking signs out every administrator except global super administrators and requires re-authentication.',
    permission: 'security.emergency',
    lastChangedBy: null,
    lastChangedAt: null,
  },
]

/* ---------------------------------------------------------------------- audit */

export const auditTrail: AuditRecord[] = [
  {
    id: 'aud-1',
    at: '2026-08-14T07:12:40Z',
    actor: '@ops.lead',
    actorRole: 'Global super administrator',
    scope: 'Campuzon global',
    action: 'Assigned role',
    resource: 'Role assignment',
    resourceId: 'asg-6',
    before: 'No risk role',
    after: 'Risk administrator (global) — awaiting approval',
    reason: 'Cover for risk review during leave.',
    result: 'succeeded',
  },
  {
    id: 'aud-2',
    at: '2026-08-14T06:31:12Z',
    actor: '@john.a',
    actorRole: 'Housing administrator',
    scope: 'FUTA · Main Campus · housing',
    action: 'Attempted to open transaction',
    resource: 'Ledger entry',
    resourceId: 'TXN-000412',
    before: null,
    after: null,
    reason: 'Following up a student housing payment query.',
    result: 'denied',
  },
  {
    id: 'aud-3',
    at: '2026-08-13T19:04:55Z',
    actor: '@david.o',
    actorRole: 'Support administrator',
    scope: 'UNILAG · Akoka Campus',
    action: 'Attempted to open identity document',
    resource: 'Identity document',
    resourceId: 'DOC-88214',
    before: null,
    after: null,
    reason: 'Verifying a student claim on a ticket.',
    result: 'denied',
  },
  {
    id: 'aud-4',
    at: '2026-08-13T15:02:18Z',
    actor: '@adaeze.k',
    actorRole: 'Trust & safety administrator',
    scope: 'Campuzon global',
    action: 'Changed verification status',
    resource: 'Housing listing',
    resourceId: 'LST-2261',
    before: 'Campuzon Verified',
    after: 'Under review',
    reason: 'Student report: room count and facilities do not match the listing.',
    result: 'succeeded',
  },
  {
    id: 'aud-5',
    at: '2026-08-13T11:22:03Z',
    actor: '@mary.f',
    actorRole: 'Finance administrator',
    scope: 'Campuzon global · finance',
    action: 'Prepared refund',
    resource: 'Approval request',
    resourceId: 'apr-2',
    before: 'No compensating entry',
    after: 'Refund prepared — awaiting a second approver',
    reason: 'Duplicate charge confirmed against provider references.',
    result: 'succeeded',
  },
  {
    id: 'aud-6',
    at: '2026-08-12T10:00:00Z',
    actor: '@ops.lead',
    actorRole: 'Global super administrator',
    scope: 'FUTA · Main Campus',
    action: 'Sent staff invitation',
    resource: 'Invitation',
    resourceId: 'inv-1',
    before: null,
    after: 'Campus housing officer — FUTA Main Campus, housing only',
    reason: 'Campus housing coverage ahead of activation.',
    result: 'succeeded',
  },
  {
    id: 'aud-7',
    at: '2026-07-01T09:14:00Z',
    actor: '@ops.lead',
    actorRole: 'Global super administrator',
    scope: 'UNILAG',
    action: 'Changed configuration',
    resource: 'Marketplace commission',
    resourceId: 'cfg-5',
    before: '7% of order value',
    after: '5% of order value',
    reason: 'Approved commercial revision for the tenant.',
    result: 'succeeded',
  },
]
